import { Controller } from "@hotwired/stimulus"

// A select we can actually style.
//
// A native <select>'s option menu is drawn by the operating system. No CSS
// reaches it — not the font, not the colors, not the padding — which is why
// the closed control can be dressed up and the menu that drops out of it
// cannot. The only fix is to stop using one. Copied from smrtr's working
// controller rather than rebuilt (standing convention).
//
// The real <select> stays in the DOM: it is what submits, and until this
// controller connects it is also what renders — `data-listbox-ready` is the
// switch that hides it and shows our button, so no JS means a native control,
// never a dead form.
export default class extends Controller {
  static targets = ["select", "button", "label", "list", "option"]

  connect() {
    this.open = false
    this.syncLabel()
    this.element.dataset.listboxReady = ""
    this.selectTarget.tabIndex = -1
    this.selectTarget.setAttribute("aria-hidden", "true")
    this.onDocClick = (e) => { if (!this.element.contains(e.target)) this.close() }
    document.addEventListener("click", this.onDocClick)
  }

  disconnect() { document.removeEventListener("click", this.onDocClick) }

  toggle(e) {
    e.preventDefault()
    this.open ? this.close() : this.show()
  }

  show() {
    this.open = true
    this.listTarget.hidden = false
    this.buttonTarget.setAttribute("aria-expanded", "true")
    // Safari does not focus a <button> on click — so after opening, keystrokes
    // were going to the page, not the component, and type-to-search was dead
    // there while Chrome (which does focus on click) worked. Take focus
    // explicitly; every keydown then bubbles through the component root.
    this.buttonTarget.focus()
    this.#place()
    this.activeIndex = this.optionTargets.findIndex((o) => o.dataset.value === this.selectTarget.value)
    this.highlight(this.activeIndex < 0 ? 0 : this.activeIndex)
  }

  close() {
    if (!this.open) return
    this.open = false
    this.listTarget.hidden = true
    this.listTarget.style.left = ""
    this.listTarget.style.right = ""
    this.buttonTarget.setAttribute("aria-expanded", "false")
  }

  // Anchor the menu to whichever edge keeps it on screen.
  //
  // The menu is `width:max-content` so it fits its longest label — which is the
  // fix for "Connecticut" clipping, and the cause of this bug. A narrow control
  // sitting at the right edge of its card (the State field is a 7.5rem column
  // there) gets a menu ~14rem wide hanging off `left:0`, and "District of
  // Columbia" pushes it past the viewport. The page then gains a horizontal
  // scrollbar, and everything centered in it — the whole founding-coach card,
  // its header — jumps sideways the instant the menu opens. Derick reported it
  // as the header changing shape on click, which is exactly what it looks like
  // from the outside: nothing near the header is at fault.
  //
  // Measured after the menu is visible, because a hidden element has no box.
  // Cleared on close so the next open re-decides — the same control can be near
  // the edge or not depending on viewport width.
  #place() {
    const list = this.listTarget
    list.style.left = "0"
    list.style.right = "auto"
    list.style.top = ""
    list.style.bottom = ""

    const width = document.documentElement.clientWidth
    const height = document.documentElement.clientHeight
    if (width <= 0 || height <= 0) return // not laid out; leave the CSS default

    const gutter = 8
    const box = list.getBoundingClientRect()

    if (box.right > width - gutter) {
      list.style.left = "auto"
      list.style.right = "0"
    }

    // Open UPWARD when the menu would hang off the bottom, and this is the
    // half that fixes the shift Derick kept seeing.
    //
    // The menu is absolutely positioned, but an absolutely positioned box still
    // counts toward its scroll container's scrollHeight. On a page that fits the
    // viewport — the founding-coach form does — a menu dropping past the fold
    // makes the document taller, a vertical scrollbar appears, and every
    // horizontally centered thing on the page re-centers into the narrower
    // viewport. That is the ~20px sideways jump of the whole card and its
    // header, and nothing about it is a bug in the header.
    //
    // `scrollbar-gutter: stable` in the layout stops the re-centering; this
    // stops the document growing in the first place, and gives a menu that
    // would have been half off screen somewhere usable to live.
    const button = this.buttonTarget.getBoundingClientRect()
    const below = height - button.bottom
    const above = button.top
    if (box.height > below - gutter && above > below) {
      list.style.top = "auto"
      list.style.bottom = "calc(100% + .3rem)"
    }
  }

  pick(e) {
    const value = e.currentTarget.dataset.value
    this.selectTarget.value = value
    // Fire the event a real select would, so anything listening still hears it.
    this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.syncLabel()
    this.close()
    this.buttonTarget.focus()
  }

  keys(e) {
    // Type-to-search, on the visible label AND the submitted value — "NC"
    // lands on North Carolina whichever one you know, and Enter takes it
    // (standing convention, Derick 2026-08-02: every dropdown searches).
    // Space joins an in-flight query ("track f…"); on its own it stays the
    // open/select key it has always been.
    if (this.#typeahead(e)) return

    const keys = ["ArrowDown", "ArrowUp", "Enter", " ", "Escape", "Home", "End"]
    if (!keys.includes(e.key)) return
    if (!this.open && (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")) {
      e.preventDefault(); return this.show()
    }
    if (!this.open) return
    e.preventDefault()
    const last = this.optionTargets.length - 1
    if (e.key === "Escape") { this.close(); this.buttonTarget.focus() }
    else if (e.key === "ArrowDown") this.highlight(Math.min(this.activeIndex + 1, last))
    else if (e.key === "ArrowUp") this.highlight(Math.max(this.activeIndex - 1, 0))
    else if (e.key === "Home") this.highlight(0)
    else if (e.key === "End") this.highlight(last)
    else if (e.key === "Enter" || e.key === " ") this.optionTargets[this.activeIndex].click()
  }

  #typeahead(e) {
    const printable = e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey
    if (!printable) return false
    if (e.key === " " && !this.query) return false

    e.preventDefault()
    clearTimeout(this.queryTimer)
    this.query = (this.query || "") + e.key.toLowerCase()
    // The buffer outlives a hunting pause but not a change of mind.
    this.queryTimer = setTimeout(() => { this.query = "" }, 700)

    if (!this.open) this.show()

    const q = this.query
    const opts = this.optionTargets
    const label = (o) => o.dataset.label.toLowerCase()
    const value = (o) => String(o.dataset.value).toLowerCase()
    // Label prefix beats value prefix beats label substring — the order a
    // person most likely means when they start typing.
    let i = opts.findIndex((o) => label(o).startsWith(q))
    if (i < 0) i = opts.findIndex((o) => value(o).startsWith(q))
    if (i < 0) i = opts.findIndex((o) => label(o).includes(q))
    if (i >= 0) this.highlight(i)
    return true
  }

  highlight(i) {
    this.activeIndex = i
    this.optionTargets.forEach((o, n) => {
      o.classList.toggle("active", n === i)
      o.setAttribute("aria-selected", o.dataset.value === this.selectTarget.value ? "true" : "false")
    })
    this.optionTargets[i]?.scrollIntoView({ block: "nearest" })
  }

  syncLabel() {
    const chosen = this.optionTargets.find((o) => o.dataset.value === this.selectTarget.value)
    this.labelTarget.textContent = chosen ? chosen.dataset.label : ""
  }
}
